/**
 * Broker/Company Dashboard Page
 *
 * Main dashboard with tabs for Overview, Properties, Analytics, Team, and Settings.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useCompany } from '../../hooks/useCompany';
import { useSubscription } from '../../hooks/useSubscription';
import { useAnalytics, type DateRange } from '../../hooks/useAnalytics';
import { useUserProperties } from '../../hooks/useUserProperties';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Tabs from '../../components/base/Tabs';
import DashboardOverview from './components/DashboardOverview';
import DashboardAnalytics from './components/DashboardAnalytics';
import DashboardProperties from './components/DashboardProperties';
import { DashboardTips } from '../../components/dashboard/DashboardTips';
import { GooeyLoader } from '../../components/ui/gooey-loader';

export default function DashboardPage() {
  const { t, i18n } = useTranslation('dashboard');
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();
  const { company } = useCompany();
  const { subscription, isSubscribed } = useSubscription();
  const { total: realListingsUsed } = useUserProperties();
  const { dateRange, setDateRange, fetchAll, loading } = useAnalytics();

  const [activeTab, setActiveTab] = useState('overview');

  // Fetch analytics data on mount
  useEffect(() => {
    fetchAll();
  }, [fetchAll, dateRange]);

  const tabs = [
    { id: 'overview', label: t('tabs.overview'), icon: 'ri-dashboard-line' },
    { id: 'properties', label: t('tabs.properties'), icon: 'ri-building-line' },
    { id: 'analytics', label: t('tabs.analytics'), icon: 'ri-bar-chart-line' },
    // Team tab is only meaningful for company accounts; brokers and individuals
    // don't have a team. The Team page itself also redirects non-company users.
    ...(company ? [{ id: 'team', label: t('tabs.team'), icon: 'ri-team-line' }] : []),
  ];

  const dateRangeOptions: { value: DateRange; label: string }[] = [
    { value: '7d', label: t('dateRange.last7Days') },
    { value: '30d', label: t('dateRange.last30Days') },
    { value: '90d', label: t('dateRange.last90Days') },
    { value: '1y', label: t('dateRange.lastYear') },
    { value: 'all', label: t('dateRange.allTime') },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview />;
      case 'properties':
        return <DashboardProperties />;
      case 'analytics':
        return <DashboardAnalytics />;
      case 'team':
        return (
          <div className="text-center py-12">
            <Link
              to="/dashboard/team"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-shadow"
            >
              <i className="ri-team-line"></i>
              {t('team.title')}
            </Link>
          </div>
        );
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      {/* Dashboard Header */}
      <header className="bg-white dark:bg-[var(--pv-color-surface-strong)] shadow-sm border-b border-[var(--pv-border-soft)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-text)]">
                {t('welcomeBack')}, {user?.firstName || user?.fullName?.split(' ')[0] || t('title')}
              </h1>
              <p className="text-[var(--pv-color-text-muted)] mt-1">
                {subscription?.categoryType || (isSubscribed ? t('subscribed') : t('notSubscribed'))}
                {subscription?.maxListings && (
                  <span className="mx-2">•</span>
                )}
                {subscription?.maxListings && (
                  <span>
                    {realListingsUsed}/{subscription.maxListings} {t('stats.listings')}
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Date Range Selector */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as DateRange)}
                className="px-4 py-2 bg-white dark:bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-sm text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
              >
                {dateRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {/* Statistics Page Link — dedicated page with per-property
                  views, WhatsApp clicks, phone clicks, and inquiries. */}
              <Link
                to="/dashboard/statistics"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--pv-border-soft)] bg-white text-[var(--pv-color-text)] rounded-xl font-medium hover:border-[var(--pv-color-primary-light)] hover:text-[var(--pv-color-primary)] transition-colors"
              >
                <i className="ri-bar-chart-2-line"></i>
                <span className="hidden sm:inline">
                  {isRTL ? 'الإحصائيات' : 'Statistics'}
                </span>
              </Link>

              {/* Add Property Button */}
              <Link
                to="/properties/new"
                data-tip-target="add-listing"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-shadow"
              >
                <i className="ri-add-line"></i>
                <span className="hidden sm:inline">{t('properties.addProperty')}</span>
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6">
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              variant="underline"
              size="md"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && activeTab !== 'properties' && activeTab !== 'team' ? (
          <div className="flex items-center justify-center py-12">
            <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
          </div>
        ) : (
          renderTabContent()
        )}
      </main>

      <DashboardTips accountType="broker" />
      <Footer />
    </div>
  );
}
